// Respuesta con IA de cara al cliente — lo que muestran el panel del ticket y
// la tarjeta de ajustes. Módulo PURO (sin API ni Firebase) para poder probarlo
// en el worktree sin credenciales.
//
// Dumax no tiene base de conocimiento pública: el solicitante sin cuenta o sin
// compañía vinculada recibe SIEMPRE el acuse de recibo fijo del servidor (una
// persona le contestará) y la conversación queda traspasada. Sólo el cliente
// con cuenta y compañía recibe texto redactado por el modelo.
import type {
  AiFirstReplySettings,
  AiFirstReplyModel,
  Ticket,
  TimestampLike,
} from "./serviceDeskService";

export const AI_FIRST_REPLY_MODELS: AiFirstReplyModel[] = ["claude-haiku-4-5", "claude-sonnet-5", "claude-opus-5"];

// Debe coincidir con DEFAULT_AI_FIRST_REPLY del backend (serviceDeskConstants.js).
export const DEFAULT_AI_FIRST_REPLY: AiFirstReplySettings = {
  enabled: false,
  persona: "",
  signature: "Agente IA",
  model: "claude-haiku-4-5",
  maxTokens: 700,
  timeoutMs: 20000,
  excludedLabels: [],
  excludedCategories: [],
  excludedBrands: [],
  conversationEnabled: false,
  conversationMaxTurns: 5,
  conversationMaxCostUsd: 0.5,
};

export function withAiFirstReplyDefaults(s?: Partial<AiFirstReplySettings> | null): AiFirstReplySettings {
  return { ...DEFAULT_AI_FIRST_REPLY, ...(s || {}) };
}

function hasTimestamp(v: TimestampLike | undefined): boolean {
  return v !== undefined && v !== null && v !== "";
}

export type FirstReplyTone = "ok" | "info" | "warn" | "error" | "muted";

export interface FirstReplyView {
  /** Hay algo que mostrar (el trigger tocó el ticket o está desactivado). */
  visible: boolean;
  state: string;
  /** Clave i18n (ticketDetail.aiReply.first.*) del mensaje principal. */
  messageKey: string;
  tone: FirstReplyTone;
  /** El reloj de primera respuesta se detuvo (el cliente recibió contenido real). */
  slaStopped: boolean;
  scopeKey: string | null;
  error: string | null;
}

export function getFirstReplyView(ticket: Pick<Ticket, "aiAgent" | "aiFirstReplyDisabled">): FirstReplyView {
  const ai = ticket.aiAgent || null;
  const state = ai?.firstReplyState || "idle";
  const scopeKey = ai?.firstReplyScope ? `ticketDetail.aiReply.first.scope.${ai.firstReplyScope}` : null;
  const base = { state, scopeKey, error: ai?.firstReplyError || null, slaStopped: false };

  if (state === "sent") {
    // El acuse (sin cuenta, o texto retenido por la guardia) NO detiene el SLA.
    if (ai?.firstReplyGuard) {
      const key = ai.firstReplyGuard === "no_public_kb" ? "sentAckNoAccount" : "sentAckGuard";
      return { ...base, visible: true, messageKey: `ticketDetail.aiReply.first.${key}`, tone: "warn" };
    }
    return { ...base, visible: true, messageKey: "ticketDetail.aiReply.first.sent", tone: "ok", slaStopped: true };
  }
  if (state === "working" || state === "posting") {
    return { ...base, visible: true, messageKey: "ticketDetail.aiReply.first.working", tone: "info" };
  }
  if (state === "failed") {
    return { ...base, visible: true, messageKey: "ticketDetail.aiReply.first.failed", tone: "error" };
  }
  if (state === "skipped") {
    return { ...base, visible: true, messageKey: "ticketDetail.aiReply.first.skipped", tone: "muted" };
  }
  if (ticket.aiFirstReplyDisabled) {
    return { ...base, visible: true, messageKey: "ticketDetail.aiReply.first.disabledTicket", tone: "muted" };
  }
  return { ...base, visible: hasTimestamp(ai?.firstReplyAt), messageKey: "ticketDetail.aiReply.first.idle", tone: "muted" };
}

const HANDOFF_REASONS = [
  "customer_requested_human",
  "customer_frustrated",
  "cap_turns",
  "cap_cost",
  "not_covered",
  "unsafe_content",
];

export interface ConversationView {
  visible: boolean;
  state: string;
  turns: number;
  costUsd: number;
  handedOff: boolean;
  humanOwns: boolean;
  /** Clave i18n del motivo del traspaso (ticketDetail.aiReply.conversation.reasons.*). */
  reasonKey: string | null;
  pausedByBot: boolean;
  /** Mostrar «Reactivar»: hay traspaso o una persona es dueña del hilo, y el ticket sigue abierto. */
  canResume: boolean;
}

export function getConversationView(ticket: Pick<Ticket, "aiConversation" | "status">): ConversationView {
  const c = ticket.aiConversation || null;
  const handedOff = hasTimestamp(c?.handedOffAt);
  const humanOwns = c?.state === "human_owns";
  const reason = c?.handoffReason || null;
  const closed = ["resolved", "closed"].includes(ticket.status);
  return {
    visible: Boolean(c),
    state: c?.state || "idle",
    turns: Number(c?.turns) || 0,
    costUsd: Number(c?.costUsd) || 0,
    handedOff,
    humanOwns,
    reasonKey: reason ? `ticketDetail.aiReply.conversation.reasons.${HANDOFF_REASONS.includes(reason) ? reason : "other"}` : null,
    pausedByBot: c?.pausedByBot === true,
    canResume: !closed && (handedOff || humanOwns),
  };
}
